<script lang="ts">
  import { onDestroy } from "svelte";
  import type { SvelteMediaTimeRange } from "svelte/elements";

  import type { VideoContent } from "$lib/elev8Api/contentTypes";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";
  import { recordPlayEvent } from "$lib/dataPipeline/dataPipelineManager";
  import { contentPlayed } from "$lib/proofOfPlay/popManager";
  import { getPopUrl } from "$lib/proofOfPlay/popHelpers";

  import { domainAndFileFromUrl } from "$lib/utils/urlUtils";
  import { logger } from "$lib/utils/logger";

  interface Props {
    content: VideoContent;
    addBufferTime?: (seconds: number) => void;
  }

  let { content: _content, addBufferTime }: Props = $props();
  // svelte-ignore state_referenced_locally
  const content = _content;
  const popUrl = getPopUrl(content);

  const config = getConfigurationContext();

  let duration = $state(NaN);
  let played: SvelteMediaTimeRange[] = $state([{ start: 0, end: 0 }]);
  let maxPlayTime = $derived(played[0]?.end || 0);
  let videoElement: HTMLVideoElement | undefined = $state();

  // Only read in event handlers (buffer-time, ontimeupdate guard); not reactive.
  let videoCanPlayThrough = false;
  // Set on canplaythrough OR error, so a broken source stops the placeholder.
  let loadSettled = $state(false);
  let playRecorded = false;

  const mountedAt = performance.now();

  // canplaythrough fires on loop too; videoCanPlayThrough guards re-entry
  function oncanplaythrough() {
    if (!videoCanPlayThrough) {
      videoCanPlayThrough = true;
      loadSettled = true;
      const bufferSeconds = (performance.now() - mountedAt) / 1000;
      logger.debug(
        `[VideoContent] canplaythrough (loaded and rendered), bufferTime=${bufferSeconds.toFixed(1)}s`
      );
      if (bufferSeconds >= 1) {
        addBufferTime?.(bufferSeconds);
      }
    }
  }

  function onplay() {
    logger.debug("[VideoContent] play - playback started");
  }

  function onerror() {
    loadSettled = true;
  }

  function ontimeupdate(event: Event) {
    if (playRecorded || !videoCanPlayThrough) return;
    const el = event.currentTarget as HTMLVideoElement;
    if (!Number.isFinite(el.duration) || el.duration <= 0) return;
    if (el.currentTime / el.duration < config.settings.video_completed_ratio)
      return;
    logger.debug(
      `[VideoContent] video played. duration=${el.duration}, currentTime=${el.currentTime}, ratio=${config.settings.video_completed_ratio}`
    );
    recordPlayEvent(content);
    playRecorded = true;
    if (popUrl) {
      logger.info(
        "[VideoContent] video ad played (domain//file, pop URL): ",
        domainAndFileFromUrl(content.asset_url),
        domainAndFileFromUrl(popUrl)
      );
      contentPlayed(popUrl);
    }
  }

  onDestroy(() => {
    logger.debug("[VideoContent] onDestroy - component unmounted", content.id);
    if (!playRecorded) {
      logger.warn(
        "[VideoContent] video not played",
        `duration=${duration}, maxPlayTime=${maxPlayTime}, ratio=${config.settings.video_completed_ratio}, videoCanPlayThrough=${videoCanPlayThrough}, asset_url=${content.asset_url}`
      );
    }
    // WebKit decoder release: pause + removeAttribute + load (MDN/WebKit bug tracker)
    if (videoElement) {
      videoElement.pause();
      videoElement.removeAttribute("src");
      videoElement.load();
    }
  });
</script>

<div class="vc-content-layout">
  {#if !loadSettled}
    <div class="vc-loading-breathe" aria-hidden="true"></div>
  {/if}

  <video
    crossorigin="anonymous"
    class="vc-content-media"
    muted
    autoPlay
    loop
    src={content.asset_url}
    bind:this={videoElement}
    bind:duration
    bind:played
    {oncanplaythrough}
    {onplay}
    {ontimeupdate}
    {onerror}
  ></video>
</div>
